import { useMutation } from "@tanstack/react-query";

import { authClient, betterAuthErrorText } from "lib/auth/authClient";
import { reportToSentry, SKIP_SENTRY } from "lib/sentryErrors";

/**
 * Which sign-in methods the email's company allows. `sso` means a verified provider covers the
 * domain — read it, not `ssoProviderId`, which is null when several providers match. It implies
 * nothing about the other three: setting SSO up turns them off, but an admin can turn one back on.
 */
export type AllowedLoginMethods = {
  password: boolean;
  google: boolean;
  microsoft: boolean;
  sso: boolean;
};

export type MigrationStatus = {
  ssoProviderId: string | null;
  allowedMethods: AllowedLoginMethods;
};

// What we assume when the check couldn't answer. Permissive on the restrictable methods — the
// auth-service refuses a disallowed one anyway, whereas guessing "nothing allowed" would leave a
// user with no way in. `sso` stays false: without an answer we don't know a verified provider
// covers the domain, and offering it when none does only dead-ends at the provider.
export const DEFAULT_LOGIN_METHODS: AllowedLoginMethods = {
  password: true,
  google: true,
  microsoft: true,
  sso: false,
};

/**
 * Asks the auth-service how an email can sign in. `ssoProviderId` is the enterprise SSO provider
 * covering the email's domain, if exactly one does. `allowedMethods` says which of password /
 * Google / Microsoft / enterprise SSO the *domain* permits, so the page only offers options that
 * would work. It's a hint; the auth-service enforces the same rules.
 */
const MIGRATION_STATUS_KEY = ["migrationStatus"];

export async function fetchMigrationStatus(email: string): Promise<MigrationStatus> {
  const { data, error } = await authClient
    .$fetch<MigrationStatus>("/migration-status", {
      method: "GET",
      query: { email: email.trim() },
    })
    .catch((thrown: unknown) => {
      // A network failure or abort throws rather than resolving with `error`.
      reportToSentry(thrown, MIGRATION_STATUS_KEY);
      throw thrown;
    });
  if (error) {
    if (!error.status || error.status >= 500) {
      reportToSentry(error, MIGRATION_STATUS_KEY);
    }
    throw new Error(betterAuthErrorText(error, "Could not check your account"));
  }

  // The webapp and auth-service deploy separately, so an older auth-service may answer without
  // `allowedMethods`. There a provider id meant SSO was the only way in, so say that.
  // TODO(APP-6834): generate these types from the endpoint rather than declaring them here.
  const fallback: AllowedLoginMethods = data.ssoProviderId
    ? { password: false, google: false, microsoft: false, sso: true }
    : DEFAULT_LOGIN_METHODS;
  return { ...data, allowedMethods: data.allowedMethods ?? fallback };
}

export function useMigrationStatus() {
  return useMutation({
    // fetchMigrationStatus reports its own failures, as it is also called outside React Query.
    meta: SKIP_SENTRY,
    mutationKey: MIGRATION_STATUS_KEY,
    mutationFn: ({ email }: { email: string }) => fetchMigrationStatus(email),
  });
}
